import { useMemo } from "react";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";

import {
  type EditedPermission,
  type FundVehicleTab,
  getFundVehicleTabFromPermission,
} from "../components/collaboratorsV2Constants";

import {
  getRowKey,
  useCollaboratorRolesManager,
} from "./useCollaboratorRolesManager";

type SingleSaveCallbacks = {
  onSuccess: () => void;
  onError: (error: ErrorType<unknown>) => void;
};

export const useSingleCollaboratorRolesManager = ({
  selectedCollaborator,
  isEditing,
}: {
  selectedCollaborator: CollaboratorDetails | null;
  isEditing: boolean;
}) => {
  const collaborators = useMemo(
    () => (selectedCollaborator ? [selectedCollaborator] : []),
    [selectedCollaborator],
  );
  const multi = useCollaboratorRolesManager({ collaborators, isEditing });

  const userId = selectedCollaborator?.id ?? "";
  const view = multi.getUserState(userId);

  const findRowKeyForRoleId = (roleId: string): string | undefined => {
    const permission = selectedCollaborator?.permissions?.find(
      (entry) => entry.roleId === roleId,
    );

    if (!permission) return undefined;

    return getRowKey(
      permission.roleId,
      getFundVehicleTabFromPermission(permission),
    );
  };

  const markedForDeletionByRoleId = useMemo(
    () =>
      new Set(
        [...view.markedForDeletion]
          .map((rowKey) => rowKey.split(":")[0])
          .filter((value): value is string => Boolean(value)),
      ),
    [view.markedForDeletion],
  );

  const editedPermissionsByRoleId = useMemo<
    Record<string, EditedPermission>
  >(() => {
    const result: Record<string, EditedPermission> = {};
    Object.entries(view.editedPermissions).forEach(([rowKey, edited]) => {
      const [originalRoleId] = rowKey.split(":");

      if (originalRoleId) result[originalRoleId] = edited;
    });

    return result;
  }, [view.editedPermissions]);

  const toggleDeletion = (roleId: string) => {
    const rowKey = findRowKeyForRoleId(roleId);

    if (rowKey) view.toggleDeletion(rowKey);
  };

  const unmarkForDeletion = (roleId: string) => {
    const rowKey = findRowKeyForRoleId(roleId);

    if (rowKey) view.unmarkForDeletion(rowKey);
  };

  const removeRole = (roleId: string) => {
    const pendingCreation = view.pendingCreations.find(
      (creation) => creation.roleId === roleId,
    );

    if (!pendingCreation) return;

    const rowKey = getRowKey(
      pendingCreation.roleId,
      pendingCreation.fundVehicleTab,
    );
    view.removeRole(rowKey);
  };

  const editPermission = (
    roleId: string,
    updates: Partial<EditedPermission>,
  ) => {
    const rowKey = findRowKeyForRoleId(roleId);

    if (rowKey) view.editPermission(rowKey, updates);
  };

  const addRole = (roleId: string, fundVehicleTab: FundVehicleTab) => {
    view.addRole(roleId, fundVehicleTab);
  };

  const getDisabledRoleIdsForTab = (
    tab: FundVehicleTab,
    excludeRoleId?: string,
  ): string[] => {
    if (!excludeRoleId) return view.getDisabledRoleIdsForTab(tab);

    const hasMatchingPermission = selectedCollaborator?.permissions?.some(
      (entry) =>
        entry.roleId === excludeRoleId &&
        getFundVehicleTabFromPermission(entry) === tab,
    );
    const hasMatchingPending = view.pendingCreations.some(
      (creation) =>
        creation.roleId === excludeRoleId && creation.fundVehicleTab === tab,
    );

    if (!hasMatchingPermission && !hasMatchingPending) {
      return view.getDisabledRoleIdsForTab(tab);
    }

    const excludeRowKey = getRowKey(excludeRoleId, tab);

    return view.getDisabledRoleIdsForTab(tab, excludeRowKey);
  };

  const save = (callbacks?: SingleSaveCallbacks) => {
    if (!selectedCollaborator) return Promise.resolve();

    return multi.save({
      onSuccess: callbacks?.onSuccess,
      onPartialFailure: ([first]) => callbacks?.onError(first.error),
      onError: (error) => callbacks?.onError(error),
    });
  };

  return {
    permissionsData: view.permissionsData,
    columns: view.columns,
    collaboratorHasNoPermissions: view.collaboratorHasNoPermissions,
    hasChanges: view.hasChanges,
    hasInvalidEntries: view.hasInvalidEntries,
    markedForDeletion: markedForDeletionByRoleId,
    pendingCreations: view.pendingCreations,
    editedPermissions: editedPermissionsByRoleId,
    getDisabledRoleIdsForTab,
    getPermissionsMarkedForDeletion: view.getPermissionsMarkedForDeletion,
    addRole,
    toggleDeletion,
    unmarkForDeletion,
    removeRole,
    editPermission,
    resetChanges: view.resetChanges,
    save,
    isSaving: multi.isSaving,
    roles: multi.roles,
    isLoadingRoles: multi.isLoadingRoles,
    getRoleNameById: multi.getRoleNameById,
  };
};
